/**
 * ContactModalProvider component
 *
 * A context provider that manages the contact modal state and exposes
 * methods to open/close the modal through context.
 */
import React, { useState, useCallback, createContext, ReactNode, useMemo, lazy, Suspense } from "react";

const ContactModal = lazy(() => import("./ContactModal"));

// Define the context interface
export interface ContactModalContextType {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

// Create context for contact modal
export const ContactModalContext = createContext<
  ContactModalContextType | undefined
>(undefined);

interface ContactModalProviderProps {
  /** The child components that will have access to the context */
  children: ReactNode;
}

/**
 * ContactModalProvider component
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
const ContactModalProvider: React.FC<ContactModalProviderProps> = ({
  children,
}) => {
  const [showContactModal, setShowContactModal] = useState<boolean>(false);

  // Create a stable method for opening the modal
  const openModal = useCallback(() => {
    setShowContactModal(true);
  }, []);

  // Create a stable method for closing the modal
  const closeModal = useCallback(() => {
    setShowContactModal(false);
  }, []);

  // Context value with modal state and methods, memoized to prevent unnecessary re-renders
  const contextValue = useMemo<ContactModalContextType>(() => ({
    isOpen: showContactModal,
    openModal,
    closeModal,
  }), [showContactModal, openModal, closeModal]);

  return (
    <ContactModalContext.Provider value={contextValue}>
      {children}
      <Suspense fallback={null}>
        <ContactModal isOpen={showContactModal} onClose={closeModal} />
      </Suspense>
    </ContactModalContext.Provider>
  );
};

export default ContactModalProvider;
